import React, {Component, ReactElement} from 'react';
import {connect} from 'react-redux';

import {Toolbar} from 'primereact/toolbar';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import Item from '~/shared/src/ci/Item';
import {isHistorySearch} from '~/shared/src/utilities/modules/finder';

import {formatTimestamp} from '~/shared/src/utilities/valueFormaters';
import {
    getItemTypeDisplayName,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getStatusColorStyleObject,
    getStatusDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {StoreState} from '~/frontend/src/store';


const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Should the Schema shown.
     */
    showSchema: true as boolean,

    /**
     * Should the ItemId shown.
     */
    showId: true as boolean,

    /**
     * Should the ItemType shown.
     */
    showType: true as boolean,

    /**
     * Should the ItemKey shown.
     */
    showKey: true as boolean,

    /**
     * Should the ItemName shown.
     */
    showName: true as boolean,

    /**
     * Should the ItemStatus shown.
     */
    showStatus: true as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The item to display data and metadata for.
     */
    item? : Item;

    style?: CssStyle;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;

/**
 * Headerbar
 */
class Headerbar extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  timestamp,
                  timezone,
                  item,
                  rawMode,
                  showAdditionalRawNames,
                  showSchema,
                  showId,
                  showType,
                  showKey,
                  showName,
                  showStatus,
                  style
              }                                = this.props,
              itemTypeId                       = item?.itemTypeId ?? 0,
              leftComponents : ReactElement[]  = [],
              rightComponents : ReactElement[] = [];

        let key,
            value,
            className;

        // left
        if ( showSchema ) {
            key = __( 'YN.base.finder.schema' );

            value = schema;

            leftComponents.push(
                <label
                    key={`${uid}_schemaLabelKey`}
                    className="yn-label yn-label-key mr-2"
                >
                    {key}
                </label>
            );
            leftComponents.push(
                <label
                    key={`${uid}_schemaLabelValue`}
                    className="yn-label yn-label-value"
                >
                    {value}
                </label>
            );
        }

        if ( showType ) {
            key = __( 'YN.base.finder.item.typeLabel' );

            value = getItemTypeDisplayName( itemTypeId, schema, rawMode, showAdditionalRawNames );

            if ( leftComponents.length ) {
                leftComponents.push( <i key={`${uid}_separator${leftComponents.length}`} className="p-toolbar-separator ml-4 mr-4"/> );
            }

            leftComponents.push(
                <label
                    key={`${uid}_typeLabelKey`}
                    className="yn-label yn-label-key mr-2"
                >
                    {key}
                </label>
            );
            leftComponents.push(
                <label
                    key={`${uid}_typeLabelValue`}
                    className="yn-label yn-label-value"
                >
                    {value}
                </label>
            );
        }

        if ( showId ) {
            key = __( 'YN.base.finder.item.idLabel' );

            if ( item && item.id > 0 ) {
                value = String( item.id );
            }
            else if ( item && item?.id < 0 ) {
                value = 'NEW';
            }
            else {
                value = '';
            }

            if ( leftComponents.length ) {
                leftComponents.push( <i key={`${uid}_separator${leftComponents.length}`} className="p-toolbar-separator ml-4 mr-4"/> );
            }

            leftComponents.push(
                <label
                    key={`${uid}_idLabelKey`}
                    className="yn-label yn-label-key mr-2"
                >
                    {key}
                </label>
            );
            leftComponents.push(
                <label
                    key={`${uid}_idLabelValue`}
                    className="yn-label yn-label-value"
                >
                    {value}
                </label>
            );
        }

        if ( showKey ) {
            key = getItemKeyDisplayName( itemTypeId, schema, rawMode, showAdditionalRawNames );

            if ( item?.key ) {
                value = item.key;
            }
            else if ( item?.itemType.isKeyAutogenerated ) {
                value = item.itemType.keyDefinition ?? ''
            }
            else {
                value = '';
            }

            if ( leftComponents.length ) {
                leftComponents.push( <i key={`${uid}_separator${leftComponents.length}`} className="p-toolbar-separator ml-4 mr-4"/> );
            }

            leftComponents.push(
                <label
                    key={`${uid}_keyLabelKey`}
                    className="yn-label yn-label-key mr-2"
                >
                    {key}
                </label>
            );
            leftComponents.push(
                <label
                    key={`${uid}_keyLabelValue`}
                    className={`yn-label yn-label-value ${!item?.key && item?.itemType.isKeyAutogenerated ? 'p-text-secondary' : ''}`}
                >
                    {value}
                </label>
            );
        }

        if ( showName ) {
            key = getItemNameDisplayName( itemTypeId, schema, rawMode, showAdditionalRawNames );

            if ( item?.name ) {
                value = item.name;
            }
            else {
                value = '';
            }

            if ( leftComponents.length ) {
                leftComponents.push( <i key={`${uid}_separator${leftComponents.length}`} className="p-toolbar-separator ml-4 mr-4"/> );
            }

            leftComponents.push(
                <label
                    key={`${uid}_nameLabelKey`}
                    className="yn-label yn-label-key mr-2"
                >
                    {key}
                </label>
            );
            leftComponents.push(
                <label
                    key={`${uid}_nameLabelValue`}
                    className="yn-label yn-label-value"
                >
                    {value}
                </label>
            );
        }

        if ( showStatus ) {
            key = __( 'YN.base.finder.status' );

            if ( item?.statusId ) {
                value = getStatusDisplayName( item.statusId, schema, rawMode, showAdditionalRawNames )

                if ( timestamp && isHistorySearch( timestamp ) ) {
                    value += ` @ ${formatTimestamp( timestamp, timezone )}`;
                }

                className = getStatusColorStyleObject( item.statusId, schema );
            }
            else {
                value = '';
            }

            if ( leftComponents.length ) {
                leftComponents.push( <i key={`${uid}_separator${leftComponents.length}`} className="p-toolbar-separator ml-4 mr-4"/> );
            }

            leftComponents.push(
                <label
                    key={`${uid}_statusLabelKey`}
                    className="yn-label yn-label-key mr-2"
                >
                    {key}
                </label>
            );
            leftComponents.push(
                <label
                    key={`${uid}_statusLabelValue`}
                    className="yn-label yn-label-value"
                    style={className}
                >
                    {value}
                </label>
            );
        }

        return <Toolbar
            left={leftComponents}
            right={rightComponents}
            className="yn-panel-headerbar yn-toolbar-horizontal-separator-bottom"
            style={ { zIndex: 6, ...style }}
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user    : undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect( mapStateToProps, null )( Headerbar );
